// @givmo/social — each platform's rules, MIRRORED from the backend so the composer can say before
// anything is sent what each account will receive (SMM-WEB1; §SMM-8 point 2, "per-platform variant
// review cards with mirrored caps and counters and honest constraint hints").
//
// ⚠ THE BACKEND IS THE AUTHORITY: `app/domain/org_social.py` (SMM-BE9) re-checks every rule at
// create, edit, publish and send. This file is its mirror, rule for rule — `PLATFORM_TEXT_CAPS`,
// `outgoing_text`, `link_sent_apart`, `text_room`, `fitted_text`, `variant_text_problem`,
// `instagram_aspect_ok`, the schedule window — so the counter a person watches is the count the
// server applies. Change one with the other.
//
// ⚠ CHARACTERS ARE CODE POINTS, as Python's `len` counts them: an emoji is one character here, as
// it is there, never the two UTF-16 units `String.length` would count.

/** The platforms the first wave publishes to (the store's publishing providers). */
export const FIRST_WAVE_PLATFORMS = [
  'facebook_pages',
  'instagram',
  'linkedin',
  'test_social',
] as const;
export type SocialPlatform = (typeof FIRST_WAVE_PLATFORMS)[number];

/** Each platform's text cap, the link the send adds included (`PLATFORM_TEXT_CAPS`). */
export const PLATFORM_TEXT_CAPS: Readonly<Record<string, number>> = {
  facebook_pages: 63206,
  instagram: 2200,
  linkedin: 3000,
  test_social: 3000,
};

/** The longest cap: a variant's text never exceeds it (the column's CHECK). */
export const MAX_VARIANT_TEXT_LENGTH = 63206;
/** The base post's longest text (`org_social_post.base_text`). */
export const MAX_BASE_TEXT_LENGTH = 5000;
export const MAX_LINK_LENGTH = 2048;
/** The most accounts one post may go to. */
export const MAX_CHANNELS = 20;

/** Instagram's caption limits beside its characters. */
export const INSTAGRAM_MAX_HASHTAGS = 30;
export const INSTAGRAM_MAX_MENTIONS = 20;
/** Instagram's accepted shape, width over height: 4:5 to 1.91:1. Never cropped silently. */
export const INSTAGRAM_MIN_ASPECT = 4 / 5;
export const INSTAGRAM_MAX_ASPECT = 1.91;
/** The platforms that take no post without an image. */
export const IMAGE_REQUIRED_PLATFORMS: ReadonlySet<string> = new Set(['instagram']);

/** The upload's limits before the server makes its one JPEG (`IMAGE_MAX_UPLOAD_BYTES`, formats). */
export const IMAGE_MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
export const IMAGE_UPLOAD_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** The schedule window: at least two minutes ahead, at most 90 days (`SCHEDULE_MIN_LEAD`). */
export const SCHEDULE_MIN_LEAD_MS = 2 * 60 * 1000;
export const SCHEDULE_MAX_AHEAD_MS = 90 * 24 * 60 * 60 * 1000;

const ELLIPSIS = '…';

/** A string's length as the backend counts it: code points. */
export function charCount(text: string): number {
  return Array.from(text).length;
}

function codePoints(text: string): string[] {
  return Array.from(text);
}

/** The platform's name in a sentence or on a card. */
export function platformLabel(platform: string): string {
  switch (platform) {
    case 'facebook_pages':
      return 'Facebook';
    case 'instagram':
      return 'Instagram';
    case 'linkedin':
      return 'LinkedIn';
    case 'test_social':
      return 'Test platform';
    default:
      return platform;
  }
}

/** The kind of account a connection is, as a person names it where an organization has one of each
 *  (frame 40's "Facebook Page", "Instagram", "LinkedIn Page"). */
export function accountWord(platform: string): string {
  switch (platform) {
    case 'facebook_pages':
      return 'Facebook Page';
    case 'linkedin':
      return 'LinkedIn Page';
    default:
      return platformLabel(platform);
  }
}

export function textCap(platform: string): number {
  return PLATFORM_TEXT_CAPS[platform] ?? MAX_VARIANT_TEXT_LENGTH;
}

/** Whether the post's link travels as its own field rather than at the end of the text: a
 *  Facebook post with no image (Facebook builds the preview), and the test platform. */
export function linkSentApart(platform: string, hasImage: boolean): boolean {
  return platform === 'test_social' || (platform === 'facebook_pages' && !hasImage);
}

/** The words the platform receives: the variant's text, with the link added at the end where it
 *  does not travel apart. */
export function outgoingText(
  platform: string,
  text: string,
  linkUrl: string | null,
  hasImage: boolean,
): string {
  if (!linkUrl || linkSentApart(platform, hasImage)) return text;
  return `${text}\n\n${linkUrl}`;
}

/** How many characters of the variant's own text the platform can take, the link counted. */
export function textRoom(platform: string, linkUrl: string | null, hasImage: boolean): number {
  let cap = textCap(platform);
  if (linkUrl && !linkSentApart(platform, hasImage)) cap -= charCount(linkUrl) + 2;
  return Math.max(cap, 0);
}

/** A variant's text derived from the base: whole, or cut at a word to the platform's room with an
 *  ellipsis — what the server derives for an account with no text of its own. */
export function fittedText(
  platform: string,
  baseText: string,
  linkUrl: string | null,
  hasImage: boolean,
): string {
  const room = textRoom(platform, linkUrl, hasImage);
  const chars = codePoints(baseText);
  if (chars.length <= room) return baseText;
  if (room <= ELLIPSIS.length) return chars.slice(0, room).join('');
  let cut = chars.slice(0, room - ELLIPSIS.length);
  const space = cut.lastIndexOf(' ');
  if (space >= Math.floor((room - ELLIPSIS.length) / 2)) cut = cut.slice(0, space);
  return cut.join('').trimEnd() + ELLIPSIS;
}

// Python's `\w` is Unicode-aware: letters, digits and the underscore of any script.
const HASHTAG = /(?<![\p{L}\p{N}_&])#[\p{L}\p{N}_]+/gu;
const MENTION = /(?<![\p{L}\p{N}_])@[\p{L}\p{N}_.]+/gu;

export function hashtagCount(text: string): number {
  return text.match(HASHTAG)?.length ?? 0;
}

export function mentionCount(text: string): number {
  return text.match(MENTION)?.length ?? 0;
}

/** Why a variant cannot go to its platform as it is. */
export type VariantProblem =
  | { code: 'text_required' }
  | { code: 'text_too_long'; over: number; linkCounted: boolean }
  | { code: 'too_many_hashtags' }
  | { code: 'too_many_mentions' }
  | { code: 'image_required' }
  | { code: 'image_shape' };

export interface ImageShape {
  width: number | null;
  height: number | null;
}

export function instagramAspectOk(width: number | null, height: number | null): boolean {
  if (!width || !height) return false;
  const aspect = width / height;
  // A hair of tolerance for the rounding a resize leaves (the backend's).
  return aspect >= INSTAGRAM_MIN_ASPECT - 0.005 && aspect <= INSTAGRAM_MAX_ASPECT + 0.005;
}

/** The first rule a variant breaks for its platform, or null: the text's rules in the backend's
 *  order (`variant_text_problem`), then the image rules its create applies. */
export function variantProblem(
  platform: string,
  text: string,
  linkUrl: string | null,
  image: ImageShape | null,
): VariantProblem | null {
  const hasImage = image !== null;
  if (text.trim().length === 0) return { code: 'text_required' };
  const room = textRoom(platform, linkUrl, hasImage);
  const length = charCount(text);
  if (length > room) {
    return {
      code: 'text_too_long',
      over: length - room,
      linkCounted: Boolean(linkUrl) && room < textCap(platform),
    };
  }
  if (platform === 'instagram') {
    if (hashtagCount(text) > INSTAGRAM_MAX_HASHTAGS) return { code: 'too_many_hashtags' };
    if (mentionCount(text) > INSTAGRAM_MAX_MENTIONS) return { code: 'too_many_mentions' };
  }
  if (IMAGE_REQUIRED_PLATFORMS.has(platform)) {
    if (image === null) return { code: 'image_required' };
    if (platform === 'instagram' && !instagramAspectOk(image.width, image.height)) {
      return { code: 'image_shape' };
    }
  }
  return null;
}

/** Whether a link is one the server takes: one `https://` address, no spaces. */
export function linkProblem(value: string): 'not_https' | 'too_long' | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (!trimmed.startsWith('https://') || /\s/.test(trimmed)) return 'not_https';
  if (charCount(trimmed) > MAX_LINK_LENGTH) return 'too_long';
  return null;
}

/** Whether a time is inside the schedule window, measured from `now`. */
export function scheduleProblem(when: Date, now: Date): 'too_soon' | 'too_far' | 'invalid' | null {
  const at = when.getTime();
  if (Number.isNaN(at)) return 'invalid';
  if (at < now.getTime() + SCHEDULE_MIN_LEAD_MS) return 'too_soon';
  if (at > now.getTime() + SCHEDULE_MAX_AHEAD_MS) return 'too_far';
  return null;
}

/** A time zone the browser knows (an IANA name such as `America/New_York`), else null: an unknown
 *  or empty one falls back to the person's own zone rather than failing a schedule. */
export function knownTimeZone(zone: string | null | undefined): string | null {
  if (!zone) return null;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return null;
  }
}

/** A moment's wall-clock reading: in `zone` where one is given, else in the person's own zone. */
export interface WallTime {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export function wallTime(moment: Date, zone: string | null = null): WallTime {
  if (zone === null) {
    return {
      year: moment.getFullYear(),
      month: moment.getMonth() + 1,
      day: moment.getDate(),
      hour: moment.getHours(),
      minute: moment.getMinutes(),
      second: moment.getSeconds(),
    };
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(moment);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((entry) => entry.type === type)?.value ?? 0);
  return {
    year: part('year'),
    month: part('month'),
    day: part('day'),
    hour: part('hour') % 24,
    minute: part('minute'),
    second: part('second'),
  };
}

/** The zone's offset from UTC at a moment, in minutes east (the person's own without a zone). */
function offsetMinutes(moment: Date, zone: string | null): number {
  if (zone === null) return -moment.getTimezoneOffset();
  const wall = wallTime(moment, zone);
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
  return Math.round((asUtc - Math.floor(moment.getTime() / 1000) * 1000) / 60_000);
}

/**
 * The moment a date and a time name on a wall clock (`2026-11-20`, `09:00`): in `zone` where one is
 * given, else in the person's own zone. Read twice across the zone's offset, so a time beside a
 * daylight-saving change still lands on the instant that wall clock shows.
 */
export function wallInstant(day: string, time: string, zone: string | null = null): Date {
  if (zone === null) return new Date(`${day}T${time}`);
  const [year, month, date] = day.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  if (
    [year, month, date, hour, minute].some((value) => value === undefined || Number.isNaN(value))
  ) {
    return new Date(Number.NaN);
  }
  const named = Date.UTC(year!, month! - 1, date!, hour!, minute!);
  let moment = new Date(named - offsetMinutes(new Date(named), zone) * 60_000);
  moment = new Date(named - offsetMinutes(moment, zone) * 60_000);
  return moment;
}

/**
 * A moment as ISO 8601 WITH an offset (`2026-11-20T09:00:00-05:00`), never a bare UTC `Z` — the
 * wire the plan names ("times sent ISO-with-offset"): the person's own offset, or the given zone's.
 * The server compares in UTC either way.
 */
export function isoWithOffset(moment: Date, zone: string | null = null): string {
  const pad = (value: number, size = 2) => String(Math.abs(value)).padStart(size, '0');
  const wall = wallTime(moment, zone);
  const offset = offsetMinutes(moment, zone);
  const sign = offset >= 0 ? '+' : '-';
  return (
    `${wall.year}-${pad(wall.month)}-${pad(wall.day)}` +
    `T${pad(wall.hour)}:${pad(wall.minute)}:${pad(wall.second)}` +
    `${sign}${pad(Math.trunc(offset / 60))}:${pad(offset % 60)}`
  );
}

/** A grant family's Connect label: one Meta connect chooses a Page and its Instagram account. */
export function grantFamilyLabel(family: string, providers: readonly string[]): string {
  if (family === 'meta') return 'Facebook & Instagram';
  if (family === 'linkedin') return 'LinkedIn';
  return providers.map(platformLabel).join(' & ');
}

/** The grant family a connected account was connected through (its Reconnect's family). */
export function grantFamilyOf(provider: string): string {
  if (provider === 'facebook_pages' || provider === 'instagram') return 'meta';
  return provider;
}

/** Which of a family's offered providers starts its connect: Facebook first for Meta (one grant
 *  lists both providers' candidates), else the first the read lists. */
export function startingProvider(family: string, providers: readonly string[]): string | null {
  if (family === 'meta' && providers.includes('facebook_pages')) return 'facebook_pages';
  return providers[0] ?? null;
}
